import { localStorageUtil } from 'src/util/local-storage-util'

const STORAGE_KEY = 'grndhouse.featured-guest-journey.v1'

/**
 * How recent a completion has to be for the sign-in page to acknowledge it. Generous rather
 * than tight: the only thing a wider window risks is a friendly line for someone who finished
 * a workout and came back half an hour later. Matches mobile.
 */
const RECENT_COMPLETION_MS = 60 * 60 * 1000

type FeaturedGuestJourney = {
  plays: number
  completions: number
  /** ISO timestamp of the first guest play, so time-to-registration is computable. */
  firstPlayAt: string
  firstWorkoutId: string
  /**
   * ISO timestamp of the MOST RECENT completion, unlike `firstPlayAt` which is written once.
   * Revised every time, because it answers a different question: not "how long before they
   * signed up" but "did they just finish one" — which is what the sign-in page needs in order
   * to acknowledge it (see `justCompleted`).
   */
  lastCompletionAt?: string
}

/**
 * Properties stamped onto the account profile the first time a guest who played signs in.
 *
 * All `featured`-prefixed, because these sit on the user profile next to `email` and `gender`
 * where an unprefixed `playedAsGuest` would be anybody's guess six months from now.
 */
export type FeaturedGuestProperties = {
  featuredPlayedAsGuest: true
  featuredPlaysBeforeSignup: number
  featuredCompletionsBeforeSignup: number
  featuredFirstWorkoutId: string
  featuredFirstPlayAt: string
}

/**
 * What a signed-out visitor did with the free workouts, held in `localStorage` until they
 * create an account — at which point it is stamped onto their profile and erased.
 *
 * **Why this instead of joining identities in Mixpanel.** GH-861's question is "does letting
 * people train before they sign up make them sign up?", and answering it needs one property on
 * the account, not a merged event history. Mixpanel's own merge mechanisms are the expensive
 * way to get there: `alias` is single-use at account creation (so it needs an `isNewUser` flag
 * on three auth endpoints, and a wrong call is unrepairable), `$merge` needs a server-side
 * endpoint holding the project API secret, and Simplified ID Merge is not a settings toggle at
 * all — it requires a brand-new Mixpanel project, a new token, and manual migration of every
 * board and cohort. A boolean and three counts, written from the visitor's own browser, answer
 * the question with none of that.
 *
 * What it gives up: per-person attribution across the boundary, and Mixpanel's native
 * cross-boundary funnel. Guest-side rates become "two numbers you divide" — registrations
 * carrying `featuredPlayedAsGuest`, over unique anonymous ids that fired a featured play. It
 * does **not** give up cross-device linkage, because none of the merge options had it either:
 * a guest who plays on a phone and registers on a laptop is invisible to all of them.
 *
 * It is also a far lighter privacy footprint than binding a whole anonymous event history to a
 * named person.
 */
export const featuredGuestJourneyService = {
  /** Count a guest play, remembering the first one. */
  recordPlay: ({ workoutId }: { workoutId: string }): void => {
    const current = featuredGuestJourneyService.peek()
    localStorageUtil.setItem<FeaturedGuestJourney>(STORAGE_KEY, {
      plays: (current?.plays ?? 0) + 1,
      completions: current?.completions ?? 0,
      // `firstPlayAt` / `firstWorkoutId` are written once and never revised — the whole point
      // is the *first* thing they chose to watch, and how long before they signed up.
      firstPlayAt: current?.firstPlayAt ?? new Date().toISOString(),
      firstWorkoutId: current?.firstWorkoutId ?? workoutId,
    })
  },

  /** Count a guest completion. No-op if no play was recorded — there is nothing to complete. */
  recordCompletion: (): void => {
    const current = featuredGuestJourneyService.peek()
    if (!current) return
    localStorageUtil.setItem<FeaturedGuestJourney>(STORAGE_KEY, {
      ...current,
      completions: current.completions + 1,
      lastCompletionAt: new Date().toISOString(),
    })
  },

  /**
   * Did a signed-out visitor just finish a workout in this browser?
   *
   * The sign-in page asks this so it can acknowledge the workout instead of showing the
   * standing lead-in. A guest reaches the player *from* this page and returns to it by the
   * player's own "Back to sign in" link; without this the page reads exactly as it did before
   * they trained, at the one moment they are most disposed to register.
   *
   * Two conditions, unlike mobile's three:
   *
   * - `completions > 0` — a play is not a finish. Opening a workout and leaving has not earned
   *   an acknowledgement.
   * - within `RECENT_COMPLETION_MS` — this is about the last few minutes, not the browser's
   *   whole history. `firstPlayAt` cannot serve: it is deliberately never revised, so a
   *   returning visitor's *first* play would keep the acknowledgement alive forever.
   *
   * Mobile additionally checks account binding, because there `stampOnto` keeps the journey
   * when ATT suppresses the Mixpanel write, so a signed-in user's journey can survive. Web has
   * no such path — `clear()` runs unconditionally once the properties are stamped — so there is
   * no bound journey to guard against.
   *
   * Fails closed: a missing or unparseable timestamp yields `false`, which is the ordinary
   * lead-in. The copy is a nicety, and skipping it beats greeting the wrong person.
   */
  justCompleted: (): boolean => {
    const journey = featuredGuestJourneyService.peek()
    if (!journey || journey.completions === 0 || !journey.lastCompletionAt) return false
    const elapsed = Date.now() - new Date(journey.lastCompletionAt).getTime()
    return Number.isFinite(elapsed) && elapsed >= 0 && elapsed < RECENT_COMPLETION_MS
  },

  peek: (): FeaturedGuestJourney | undefined => localStorageUtil.getItem<FeaturedGuestJourney>(STORAGE_KEY)?.data,

  /** The profile properties for this journey, or `undefined` when there is nothing to stamp. */
  toProperties: (): FeaturedGuestProperties | undefined => {
    const journey = featuredGuestJourneyService.peek()
    if (!journey || journey.plays === 0) return undefined

    return {
      featuredPlayedAsGuest: true,
      featuredPlaysBeforeSignup: journey.plays,
      featuredCompletionsBeforeSignup: journey.completions,
      featuredFirstWorkoutId: journey.firstWorkoutId,
      featuredFirstPlayAt: journey.firstPlayAt,
    }
  },

  /**
   * Erase it. Called immediately after stamping, and it is not optional: leaving the state
   * behind would let the **next** account signed in on this browser inherit a guest journey
   * that was not theirs.
   */
  clear: (): void => localStorageUtil.removeItem(STORAGE_KEY),
}
